import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { useQuery } from 'react-query';
import { salesCyclesApi } from '../services/api';
import OpenCyclesListModal from './OpenCyclesListModal';
import { useChartTheme } from '../hooks/useChartTheme';

interface OpenCyclesTrendsData {
  period_date: string;
  period_label: string;
  newly_originated: number;
  newly_originated_no_stage_change: number;
  newly_originated_with_stage_change: number;
  old_with_touchbase: number;
  old_without_touchbase: number;
  total_active_cycles: number;
}

interface OpenCyclesTrendsChartProps {
  selectedCycleTypes?: string[];
  selectedAccountValues?: string[];
  selectedMeetingDispositions?: string[];
  selectedSalesRep?: string | null;
  selectedTeam?: string | null;
}

type PeriodType = 'week' | 'month' | 'quarter';

// Custom label renderer for stacked bars - uses dynamic font size
const renderStackedBarLabel = (fillColor: string) => (props: any) => {
  const { x, y, width, height, value } = props;

  if (!value || value === 0) {
    return null;
  }

  // Dynamic font size based on segment height
  let fontSize = 11;
  if (height < 20) {
    fontSize = 8;
  } else if (height < 30) {
    fontSize = 9;
  } else if (height < 40) {
    fontSize = 10;
  }

  return (
    <text
      x={x + width / 2}
      y={y + height / 2 + fontSize / 3}
      textAnchor="middle"
      fontSize={fontSize}
      fontFamily="Inter"
      fontWeight={700}
      fill={fillColor}
    >
      {value}
    </text>
  );
};

// Custom label renderer for single bars - always shows label above the bar
const renderBarLabelAbove = (fillColor: string) => (props: any) => {
  const { x, y, width, value } = props;

  if (!value || value === 0) {
    return null;
  }

  return (
    <text
      x={x + width / 2}
      y={y - 5}
      textAnchor="middle"
      fontSize={11}
      fontFamily="Inter"
      fontWeight={700}
      fill={fillColor}
    >
      {value}
    </text>
  );
};

const OpenCyclesTrendsChart: React.FC<OpenCyclesTrendsChartProps> = ({
  selectedCycleTypes,
  selectedAccountValues,
  selectedMeetingDispositions,
  selectedSalesRep,
  selectedTeam
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('week');
  const chartTheme = useChartTheme();
  const [selectedDataPoint, setSelectedDataPoint] = useState<OpenCyclesTrendsData | null>(null);
  const [modalCategory, setModalCategory] = useState<'newly_originated' | 'old_with_touchbase' | 'old_without_touchbase' | 'all'>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Check if user is authenticated
  const isAuthenticated = !!localStorage.getItem('salesai-api-key');

  // Convert cycle types array to comma-separated string for API calls
  const cycleTypesString = selectedCycleTypes && selectedCycleTypes.length > 0 ? selectedCycleTypes.join(',') : undefined;

  // Parse meeting dispositions to extract qualification type if present
  // Combined values like TRANSACTIONAL_DEAL need to be split into disposition + qualification_type
  const parseDispositions = (dispositions?: string[]): { meetingDispositions: string[], qualificationType: 'DEAL' | 'ZERO' | null } => {
    if (!dispositions || dispositions.length === 0) {
      return { meetingDispositions: [], qualificationType: null };
    }

    const parsedDispositions: string[] = [];
    // Track which qualification types are selected for each disposition
    const qualTypes: Set<string> = new Set();
    // Track if any disposition without qualification type variants is selected
    // (e.g., TRANSACTIONAL without _DEAL/_ZERO suffix, or NURTURE, BAD_FIT, etc.)
    let hasNonQualTypedDisposition = false;

    for (const d of dispositions) {
      if (d === 'TRANSACTIONAL_DEAL') {
        parsedDispositions.push('TRANSACTIONAL');
        qualTypes.add('DEAL');
      } else if (d === 'TRANSACTIONAL_ZERO') {
        parsedDispositions.push('TRANSACTIONAL');
        qualTypes.add('ZERO');
      } else if (d === 'ENTERPRISE_DEAL') {
        parsedDispositions.push('ENTERPRISE');
        qualTypes.add('DEAL');
      } else if (d === 'ENTERPRISE_ZERO') {
        parsedDispositions.push('ENTERPRISE');
        qualTypes.add('ZERO');
      } else {
        // Regular disposition without qualification type variant
        // (TRANSACTIONAL, ENTERPRISE, NURTURE, BAD_FIT, TRASH, NOT_ANALYZED, etc.)
        parsedDispositions.push(d);
        hasNonQualTypedDisposition = true;
      }
    }

    // Remove duplicates
    const uniqueDispositions = Array.from(new Set(parsedDispositions));

    // Only apply qualification type filter if:
    // 1. Exactly one qualification type is selected (DEAL or ZERO)
    // 2. AND no non-qual-typed dispositions are selected (which would need all qualification types)
    let qualType: 'DEAL' | 'ZERO' | null = null;
    if (qualTypes.size === 1 && !hasNonQualTypedDisposition) {
      qualType = qualTypes.has('DEAL') ? 'DEAL' : 'ZERO';
    }
    // If qualTypes.size === 0 or qualTypes.size === 2 or hasNonQualTypedDisposition, qualType stays null (no filtering)

    return { meetingDispositions: uniqueDispositions, qualificationType: qualType };
  };

  const { meetingDispositions: parsedMeetingDispositions, qualificationType } = parseDispositions(selectedMeetingDispositions);

  const { data: trendsData, isLoading, error, isFetching } = useQuery<OpenCyclesTrendsData[]>(
    [
      'openCyclesTrends',
      selectedPeriod,
      cycleTypesString || 'all',
      selectedAccountValues?.join(','),
      parsedMeetingDispositions?.join(','),
      qualificationType,
      selectedSalesRep,
      selectedTeam
    ],
    () => salesCyclesApi.getOpenCyclesTrends(
      selectedPeriod,
      12, // weeks/months/quarters to look back
      cycleTypesString,
      selectedAccountValues && selectedAccountValues.length > 0 ? selectedAccountValues.join(',') : undefined,
      selectedSalesRep,
      selectedTeam,
      parsedMeetingDispositions.length > 0 ? parsedMeetingDispositions : undefined,
      qualificationType
    ),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: true,
      refetchInterval: 5 * 60 * 1000, // Poll every 5 minutes for fresh data
      enabled: isAuthenticated
    }
  );

  // Custom tooltip component for individual charts
  const CustomTooltip = ({ active, payload, label, categoryName, color }: any) => {
    if (active && payload && payload.length) {
      const value = payload[0].value;
      return (
        <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
          <p className="text-gray-900 dark:text-gray-100 font-semibold font-inter text-sm mb-1">{label}</p>
          <div className="flex items-center justify-between space-x-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
              <span className="text-sm text-gray-600 dark:text-gray-400 font-inter">{categoryName}:</span>
            </div>
            <span className="text-sm font-semibold font-inter" style={{ color }}>{value}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const periodOptions = [
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'quarter', label: 'Quarterly' }
  ] as const;

  // Handle bar click to view sales cycles for that specific category
  const handleBarClick = (category: 'newly_originated' | 'old_with_touchbase' | 'old_without_touchbase') => (data: any) => {
    if (data && data.payload) {
      setSelectedDataPoint(data.payload);
      setModalCategory(category);
      setIsModalOpen(true);
    }
  };

  if (isLoading) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <div className="text-gray-700 dark:text-gray-300 text-sm font-inter font-medium">Loading trends data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-red-600 dark:text-red-400 text-lg font-semibold font-inter mb-2">Error loading trends</div>
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">Please try again later</div>
          </div>
        </div>
      </div>
    );
  }

  if (!trendsData || trendsData.length === 0) {
    return (
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales Cycle Activity Trends</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter">No data available for selected filters</p>
          </div>

          {/* Period Selector */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedPeriod(option.value)}
                className={`px-4 py-2 rounded-md text-sm font-medium font-inter transition-colors ${
                  selectedPeriod === option.value
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="text-gray-600 dark:text-gray-400 text-sm font-inter">No open cycles found matching the selected criteria</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`${chartTheme.cardBg} border ${chartTheme.cardBorder} rounded-xl p-6 shadow-sm`}>
        <div className="flex items-start justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 font-plus-jakarta">Sales Cycle Activity Trends</h3>
            <p className="text-gray-600 dark:text-gray-400 text-sm font-inter">Sales cycles breakdown by creation and engagement patterns</p>
          </div>

          {/* Period Selector */}
          <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
            {periodOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => setSelectedPeriod(option.value)}
                disabled={isFetching}
                className={`px-4 py-2 rounded-md text-sm font-medium font-inter transition-colors ${
                  selectedPeriod === option.value
                    ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                } ${isFetching ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Charts */}
        <div className="mt-6 space-y-6">
          {isFetching && (
            <div className="absolute top-6 right-6 flex items-center space-x-2 text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600"></div>
              <span className="text-xs font-inter">Updating...</span>
            </div>
          )}

          {/* Chart 1: New Sales Cycles Created */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-inter mb-1">New Sales Cycles Created</h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 font-inter mb-3">
              Counts how many new sales cycles were created each {selectedPeriod}.
              <span className="font-semibold text-purple-400"> Lighter purple:</span> new cycles with no open deal.
              <span className="font-semibold text-purple-600"> Darker purple:</span> new cycles with a deal opened in the same {selectedPeriod}.
            </p>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={trendsData}
                margin={{ top: 25, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
                <XAxis
                  dataKey="period_label"
                  stroke="#6b7280"
                  style={{ fontSize: '11px', fontFamily: 'Inter' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '11px', fontFamily: 'Inter' }}
                />
<Tooltip content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const noStageChange = Number(payload.find((p: any) => p.dataKey === 'newly_originated_no_stage_change')?.value || 0);
                    const withStageChange = Number(payload.find((p: any) => p.dataKey === 'newly_originated_with_stage_change')?.value || 0);
                    const total = noStageChange + withStageChange;
                    return (
                      <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                        <p className="text-gray-900 dark:text-gray-100 font-semibold font-inter text-sm mb-2">{label}</p>
                        <div className="space-y-1">
                          <div className="flex items-center justify-between space-x-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#c084fc' }}></div>
                              <span className="text-xs text-gray-600 dark:text-gray-400 font-inter">New, deal opened:</span>
                            </div>
                            <span className="text-sm font-semibold font-inter text-purple-600">{withStageChange}</span>
                          </div>
                          <div className="flex items-center justify-between space-x-4">
                            <div className="flex items-center space-x-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: '#e9d5ff' }}></div>
                              <span className="text-xs text-gray-600 dark:text-gray-400 font-inter">New, no deal:</span>
                            </div>
                            <span className="text-sm font-semibold font-inter text-purple-400">{noStageChange}</span>
                          </div>
                          <div className="pt-1 mt-1 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between space-x-4">
                              <span className="text-xs font-semibold text-gray-700 dark:text-gray-300 font-inter">Total:</span>
                              <span className="text-sm font-bold font-inter text-gray-900 dark:text-gray-100">{total}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                }} />
                <Bar
                  dataKey="newly_originated_no_stage_change"
                  stackId="newCycles"
                  fill="#e9d5ff"
                  stroke="#9333ea"
                  strokeWidth={1}
                  radius={[0, 0, 0, 0]}
                  cursor="pointer"
                  onClick={handleBarClick('newly_originated')}
                >
                  <LabelList
                    dataKey="newly_originated_no_stage_change"
                    content={renderStackedBarLabel('#581c87')}
                  />
                </Bar>
                <Bar
                  dataKey="newly_originated_with_stage_change"
                  stackId="newCycles"
                  fill="#c084fc"
                  stroke="#9333ea"
                  strokeWidth={1}
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={handleBarClick('newly_originated')}
                >
                  <LabelList
                    dataKey="newly_originated_with_stage_change"
                    content={renderStackedBarLabel('#ffffff')}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 2: Old Cycles Without Touch Base */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-inter mb-1">Old Cycles Without Touch Base</h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 font-inter mb-3">
              Counts open sales cycles with no deals that had no activity or events during a given {selectedPeriod}.<br />
              It finds sales cycles that were stuck or neglected — still open on a given {selectedPeriod} but not being worked on during that same {selectedPeriod}.
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={trendsData}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
                <XAxis
                  dataKey="period_label"
                  stroke="#6b7280"
                  style={{ fontSize: '11px', fontFamily: 'Inter' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '11px', fontFamily: 'Inter' }}
                />
<Tooltip content={(props) => <CustomTooltip {...props} categoryName="Without Touch Base" color="#f97316" />} />
                <Bar
                  dataKey="old_without_touchbase"
                  fill="#fed7aa"
                  stroke="#f97316"
                  strokeWidth={1}
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={handleBarClick('old_without_touchbase')}
                >
                  <LabelList
                    dataKey="old_without_touchbase"
                    content={renderBarLabelAbove('#9a3412')}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Chart 3: Old Cycles With Touch Base */}
          <div>
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 font-inter mb-1">Old Cycles With Touch Base</h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 font-inter mb-3">
              Counts open sales cycles with no deals that had at least one event during a given {selectedPeriod}.<br />
              It finds sales cycles that were still open on a given {selectedPeriod} and actively engaged during that same {selectedPeriod}.
            </p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={trendsData}
                margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.gridStroke} />
                <XAxis
                  dataKey="period_label"
                  stroke="#6b7280"
                  style={{ fontSize: '11px', fontFamily: 'Inter' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '11px', fontFamily: 'Inter' }}
                />
<Tooltip content={(props) => <CustomTooltip {...props} categoryName="With Touch Base" color="#10b981" />} />
                <Bar
                  dataKey="old_with_touchbase"
                  fill="#a7f3d0"
                  stroke="#10b981"
                  strokeWidth={1}
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={handleBarClick('old_with_touchbase')}
                >
                  <LabelList
                    dataKey="old_with_touchbase"
                    content={renderBarLabelAbove('#065f46')}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Modal for viewing sales cycles */}
      {selectedDataPoint && (
        <OpenCyclesListModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedDataPoint(null);
          }}
          periodLabel={selectedDataPoint.period_label}
          periodDate={selectedDataPoint.period_date}
          period={selectedPeriod}
          category={modalCategory}
          categoryDisplayName={
            modalCategory === 'newly_originated'
              ? 'Newly Originated'
              : modalCategory === 'old_with_touchbase'
              ? 'Old with Touch-Base'
              : modalCategory === 'old_without_touchbase'
              ? 'Old without Touch-Base'
              : 'All Sales Cycles'
          }
          selectedCycleTypes={selectedCycleTypes}
          selectedAccountValues={selectedAccountValues}
          selectedMeetingDispositions={selectedMeetingDispositions}
          selectedSalesRep={selectedSalesRep}
          selectedTeam={selectedTeam}
        />
      )}
    </>
  );
};

export default OpenCyclesTrendsChart;
